import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';

interface ContainerProps<T> {
  data: T[];
  children?: React.ReactNode;
  onDragEnd: (event: DragEndEvent) => void;
  renderItems?: (items: T[]) => React.ReactNode;
}

const DraggableContainer = <T,>({
  data,
  children,
  onDragEnd,
  renderItems,
}: ContainerProps<T>) => {
  const [localSortedItems, setLocalSortedItems] = useState<T[]>([]);

  useEffect(() => {
    setLocalSortedItems(data);
  }, [data]);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleOnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const newOrderRows = [...localSortedItems];
      const oldIndex = newOrderRows.findIndex(
        (element: any) => String(element.id) === active.id,
      );
      const newIndex = newOrderRows.findIndex(
        (element: any) => String(element.id) === over?.id,
      );
      newOrderRows.splice(newIndex, 0, newOrderRows.splice(oldIndex, 1)[0]);

      setLocalSortedItems(newOrderRows);
      onDragEnd(event);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleOnDragEnd}
      modifiers={[restrictToFirstScrollableAncestor]}>
      <SortableContext
        items={localSortedItems.map((element: any) => `${element.id}`)}>
        {renderItems ? renderItems(localSortedItems) : children}
      </SortableContext>
    </DndContext>
  );
};

export default DraggableContainer;
